import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { roomAPI, applicationAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { MapPin, Wifi, Car, Droplets, Zap, Home, Calendar, DollarSign, ChevronLeft, ChevronRight, Share2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from '../hooks/use-toast';
import OptimizedImage, { ThumbnailImage } from '../components/OptimizedImage';

const RoomDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [showApplication, setShowApplication] = useState(false);
  const [applicationData, setApplicationData] = useState({
    fullName: '',
    email: '',
    phone: '',
    currentAddress: '',
    moveInDate: '',
    monthlyIncome: '',
    furnitureOptions: {
      boxSpring: false,
      mattress: false,
      dresser: false,
      nightStand: false
    },
    priorConviction: null, // 'yes' | 'no' — required
    convictionDetails: '',
    priorEviction: null, // 'yes' | 'no' — required
    evictionDetails: '',
    idDocument: null,
    incomeDocument: null,
    additionalInfo: ''
  });

  useEffect(() => {
    fetchRoomDetails();
  }, [id]);

  const fetchRoomDetails = async () => {
    try {
      setLoading(true);
      const response = await roomAPI.getRoom(id);
      const data = response.data;

      // If someone hits a direct link to a room that's already booked, don't
      // show the details page — bounce them to Browse Rooms with a friendly
      // toast so they can see what's still available.
      const bookedStatuses = ['Occupied', 'Rented', 'Unavailable'];
      if (data && bookedStatuses.includes(data.status)) {
        toast({
          title: "This room is no longer available",
          description: "It has already been booked. Browsing other available rooms…",
        });
        navigate('/browse-rooms', { replace: true });
        return;
      }

      setRoom(data);
    } catch (error) {
      console.error('Error fetching room:', error);
      // If the room was deleted or is otherwise inaccessible, send them to browse
      if (error?.response?.status === 404) {
        toast({
          title: "Room not found",
          description: "That listing is no longer available.",
        });
        navigate('/browse-rooms', { replace: true });
        return;
      }
      toast({
        title: "Error",
        description: "Failed to load room details",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get full image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath || imagePath.trim() === '') return null;
    if (imagePath.startsWith('http')) return imagePath;
    // Add cache-busting parameter to prevent caching issues
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    return `${baseUrl}${imagePath}`;
  };

  // Filter out invalid images
  const validImages = (room?.images || []).filter(img => {
    if (!img || img.trim() === '') return false;
    // Check if it's a valid URL format
    if (img.startsWith('http') || img.startsWith('/api/')) return true;
    return false;
  });
  
  // State to track failed images
  const [failedImages, setFailedImages] = React.useState(new Set());
  
  // Handle image load error with retry
  const handleImageError = (e, imagePath) => {
    const img = e.target;
    const retryCount = parseInt(img.dataset.retryCount || '0');
    
    if (retryCount < 2) {
      // Retry with cache-busting
      img.dataset.retryCount = retryCount + 1;
      const originalSrc = getImageUrl(imagePath);
      if (originalSrc) {
        setTimeout(() => {
          img.src = `${originalSrc}${originalSrc.includes('?') ? '&' : '?'}retry=${retryCount + 1}&t=${Date.now()}`;
        }, 500 * (retryCount + 1));
      }
    } else {
      // After 2 retries, show fallback
      img.onerror = null;
      img.src = 'https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?w=800';
      setFailedImages(prev => new Set([...prev, imagePath]));
    }
  };

  // Share room functionality
  const handleShare = async () => {
    const url = window.location.href;
    const title = room?.title || 'Room for Rent';
    const description = `Check out this room: ${title} - $${room?.rent}/month at ${room?.address || 'Top Spot Properties'}`;
    // IMPORTANT: iOS Share Sheet targets like iMessage sometimes drop the
    // `url` field and use only `text`. To guarantee the URL is always in
    // whatever gets shared, embed it inside the text as well.
    const shareData = {
      title,
      text: `${description}\n${url}`,
      url,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        toast({ title: "Shared!", description: "Room link shared" });
      } else {
        // Fallback: copy the full text + URL to clipboard
        await navigator.clipboard.writeText(shareData.text);
        toast({ title: "Link Copied!", description: "Room link copied to clipboard" });
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Share failed:', error);
        try {
          await navigator.clipboard.writeText(shareData.text);
          toast({ title: "Link Copied!", description: "Room link copied to clipboard" });
        } catch {
          toast({ title: "Error", description: "Could not share room", variant: "destructive" });
        }
      }
    }
  };

  // Format date safely
  const formatDate = (dateString) => {
    if (!dateString) return 'Contact for availability';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Contact for availability';
      return date.toLocaleDateString('en-US', { timeZone: 'America/New_York' });
    } catch {
      return 'Contact for availability';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Loading room details...</p>
      </div>
    );
  }

  if (!room) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Room not found</h2>
          <Button onClick={() => navigate('/browse-rooms')}>Back to Browse</Button>
        </div>
      </div>
    );
  }

  const nextImage = () => {
    if (validImages.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % validImages.length);
    }
  };

  const prevImage = () => {
    if (validImages.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + validImages.length) % validImages.length);
    }
  };

  const handleApply = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (submitting) return;
    
    setSubmitting(true);
    
    try {
      // Validate required fields before submission
      if (!applicationData.fullName || !applicationData.email || !applicationData.phone || 
          !applicationData.currentAddress || !applicationData.monthlyIncome || !applicationData.moveInDate) {
        toast({
          title: "Missing Information",
          description: "Please fill in all required fields.",
          variant: "destructive"
        });
        setSubmitting(false);
        return;
      }

      // Validate required supporting documents
      if (!applicationData.idDocument) {
        toast({
          title: "ID Document Required",
          description: "Please upload a valid ID document (Driver's License, State ID, or Passport).",
          variant: "destructive"
        });
        setSubmitting(false);
        return;
      }

      if (!applicationData.incomeDocument) {
        toast({
          title: "Proof of Income Required",
          description: "Please upload proof of income (Pay Stubs, Bank Statements, or Employment Letter).",
          variant: "destructive"
        });
        setSubmitting(false);
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(applicationData.email)) {
        toast({
          title: "Invalid Email",
          description: "Please enter a valid email address.",
          variant: "destructive"
        });
        setSubmitting(false);
        return;
      }

      // Create FormData to send files along with application data
      const formData = new FormData();
      formData.append('room_id', id);
      formData.append('applicant_name', applicationData.fullName.trim());
      formData.append('email', applicationData.email.trim().toLowerCase());
      formData.append('phone', applicationData.phone.trim());
      formData.append('current_address', applicationData.currentAddress.trim());
      
      // Handle monthly income - ensure it's a valid number
      const income = parseFloat(applicationData.monthlyIncome);
      if (isNaN(income) || income <= 0) {
        toast({
          title: "Invalid Income",
          description: "Please enter a valid monthly income amount.",
          variant: "destructive"
        });
        setSubmitting(false);
        return;
      }
      formData.append('monthly_income', income);
      
      // Handle move-in date
      const moveInDate = new Date(applicationData.moveInDate);
      if (isNaN(moveInDate.getTime())) {
        toast({
          title: "Invalid Date",
          description: "Please select a valid move-in date.",
          variant: "destructive"
        });
        setSubmitting(false);
        return;
      }
      formData.append('move_in_date', moveInDate.toISOString());
      
      formData.append('furniture_box_spring', applicationData.furnitureOptions.boxSpring);
      formData.append('furniture_mattress', applicationData.furnitureOptions.mattress);
      formData.append('furniture_dresser', applicationData.furnitureOptions.dresser);
      formData.append('furniture_night_stand', applicationData.furnitureOptions.nightStand);

      // Prior conviction disclosure (required)
      if (applicationData.priorConviction !== 'yes' && applicationData.priorConviction !== 'no') {
        toast({
          title: "Answer Required",
          description: "Please answer the prior conviction question.",
          variant: "destructive"
        });
        setSubmitting(false);
        return;
      }
      const hasConviction = applicationData.priorConviction === 'yes';
      if (hasConviction && !applicationData.convictionDetails.trim()) {
        toast({
          title: "Details Required",
          description: "Please provide the date, location, and description of the conviction.",
          variant: "destructive"
        });
        setSubmitting(false);
        return;
      }
      formData.append('has_prior_conviction', hasConviction);
      formData.append('conviction_details', hasConviction ? applicationData.convictionDetails.trim() : '');

      // Prior eviction disclosure (required)
      if (applicationData.priorEviction !== 'yes' && applicationData.priorEviction !== 'no') {
        toast({
          title: "Answer Required",
          description: "Please answer the prior eviction question.",
          variant: "destructive"
        });
        setSubmitting(false);
        return;
      }
      const hasEviction = applicationData.priorEviction === 'yes';
      if (hasEviction && !applicationData.evictionDetails.trim()) {
        toast({
          title: "Details Required",
          description: "Please provide the date, location, and description of the eviction.",
          variant: "destructive"
        });
        setSubmitting(false);
        return;
      }
      formData.append('has_prior_eviction', hasEviction);
      formData.append('eviction_details', hasEviction ? applicationData.evictionDetails.trim() : '');

      formData.append('additional_info', applicationData.additionalInfo || '');
      
      // Add files if provided
      if (applicationData.idDocument) {
        console.log('ID Document:', applicationData.idDocument.name, applicationData.idDocument.type, applicationData.idDocument.size);
        formData.append('id_document', applicationData.idDocument);
      }
      if (applicationData.incomeDocument) {
        console.log('Income Document:', applicationData.incomeDocument.name, applicationData.incomeDocument.type, applicationData.incomeDocument.size);
        formData.append('income_document', applicationData.incomeDocument);
      }

      console.log('Submitting application for room:', id);
      console.log('FormData entries:');
      for (let [key, value] of formData.entries()) {
        if (value instanceof File) {
          console.log(`  ${key}: File(${value.name}, ${value.type}, ${value.size} bytes)`);
        } else {
          console.log(`  ${key}: ${value}`);
        }
      }
      const response = await applicationAPI.submitWithDocuments(formData);
      console.log('Application response:', response);
      
      // Success - show confirmation with green success toast
      toast({
        title: "Application Confirmed! ✅",
        description: "Thank you! We've received your application and will contact you within 1-2 business days. Please check your email inbox AND junk/spam folder for your confirmation.",
        duration: 10000,
        variant: "success",
      });
      
      // Reset form and close dialog
      setApplicationData({
        fullName: '',
        email: '',
        phone: '',
        currentAddress: '',
        moveInDate: '',
        monthlyIncome: '',
        furnitureOptions: {
          boxSpring: false,
          mattress: false,
          dresser: false,
          nightStand: false
        },
        priorConviction: null,
        convictionDetails: '',
        priorEviction: null,
        evictionDetails: '',
        idDocument: null,
        incomeDocument: null,
        additionalInfo: ''
      });
      setShowApplication(false);
    } catch (error) {
      console.error('Application error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Check if it's actually a success (application went through but response had issues)
      if (error.response?.status === 201 || error.response?.status === 200) {
        toast({
          title: "Application Confirmed! ✅",
          description: "Your application has been received. Please check your email for confirmation.",
          duration: 8000,
          variant: "success",
        });
        setShowApplication(false);
      } else {
        // Get the specific error message from the backend
        let errorMessage = "Something went wrong. Please check your information and try again.";
        if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        // Check for common issues
        if (error.message?.includes('Network Error') || error.code === 'ERR_NETWORK') {
          errorMessage = "Network error. Please check your internet connection and try again.";
        } else if (error.message?.includes('timeout') || error.code === 'ECONNABORTED') {
          errorMessage = "Upload timed out. Please check your internet connection or try with a stronger WiFi signal.";
        }
        
        console.error('Displaying error:', errorMessage);
        toast({
          title: "Application Could Not Be Submitted",
          description: errorMessage,
          variant: "destructive"
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const amenityIcons = {
    'WiFi': Wifi,
    'Parking': Car,
    'Utilities Included': Droplets,
    'Washer and Dryer': Home,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <Button variant="ghost" onClick={() => navigate('/browse-rooms')}>
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back to Browse
          </Button>
          
          {/* Share Button */}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleShare}>
              <Share2 className="h-4 w-4 mr-2" />
              Share
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Images and Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image Gallery */}
            <Card className="overflow-hidden">
              <div className="relative h-96 bg-gray-200">
                <OptimizedImage
                  src={validImages.length > 0 ? getImageUrl(validImages[currentImageIndex]) : null}
                  alt={room.title}
                  className="w-full h-full"
                  loading="eager"
                />
                {validImages.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-colors"
                    >
                      <ChevronLeft className="h-6 w-6" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-colors"
                    >
                      <ChevronRight className="h-6 w-6" />
                    </button>
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                      {currentImageIndex + 1} / {validImages.length}
                    </div>
                  </>
                )}
              </div>
              {validImages.length > 0 && (
                <div className="p-4 flex gap-2 overflow-x-auto">
                  {validImages.map((img, idx) => {
                    // Use thumbnail version for faster loading, with original as fallback
                    const thumbUrl = img.startsWith('/api/uploads/') 
                      ? img.replace('/api/uploads/', '/api/uploads/thumb/')
                      : img;
                    const originalUrl = getImageUrl(img);
                    return (
                      <ThumbnailImage
                        key={idx}
                        src={getImageUrl(thumbUrl)}
                        fallbackSrc={originalUrl}
                        alt={`Thumbnail ${idx + 1}`}
                        isSelected={idx === currentImageIndex}
                        onClick={() => setCurrentImageIndex(idx)}
                      />
                    );
                  })}
                </div>
              )}
            </Card>

            {/* Room Details */}
            <Card>
              <CardContent className="pt-6">
                <h1 className="text-3xl font-bold mb-2">{room.title}</h1>
                <p className="text-gray-600 mb-4 flex items-center">
                  <MapPin className="h-5 w-5 mr-2" />
                  {room.property_address || room.address || 'Address available upon inquiry'}
                  {(room.property_city || room.city) && `, ${room.property_city || room.city}`}
                  {(room.property_state || room.state) && `, ${room.property_state || room.state}`}
                  {(room.property_zip || room.zip) && ` ${room.property_zip || room.zip}`}
                </p>
                <div className="flex items-center gap-4 mb-6">
                  <Badge className="bg-green-500">{room.status}</Badge>
                  <span className="text-gray-600">{room.bath_type} Bathroom</span>
                  <span className="text-gray-600">{room.square_feet} sq ft</span>
                </div>

                <div className="border-t pt-6">
                  <h2 className="text-xl font-semibold mb-3">Description</h2>
                  <p className="text-gray-700 leading-relaxed">{room.description}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Booking Card */}
          <div className="lg:col-span-1">
            <Card className="sticky top-20">
              <CardContent className="pt-6">
                <div className="mb-6">
                  <p className="text-gray-600 mb-2">Monthly Rent</p>
                  <p className="text-4xl font-bold text-gray-900 mb-1">${room.rent}</p>
                  <p className="text-sm text-gray-600">All-inclusive pricing</p>
                </div>

                <div className="space-y-3 mb-6 pb-6 border-b">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Deposit:</span>
                    <span className="font-semibold">${room.deposit}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Available:</span>
                    <span className="font-semibold">{formatDate(room.available_date)}</span>
                  </div>
                </div>

                <Dialog open={showApplication} onOpenChange={setShowApplication}>
                  <DialogTrigger asChild>
                    <Button className="w-full bg-green-600 hover:bg-green-700 mb-3" size="lg">
                      Apply Now
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Apply for {room.title}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleApply} className="space-y-4 mt-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="fullName">Full Name *</Label>
                          <Input
                            id="fullName"
                            required
                            value={applicationData.fullName}
                            onChange={(e) => setApplicationData({...applicationData, fullName: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label htmlFor="email">Email *</Label>
                          <Input
                            id="email"
                            type="email"
                            required
                            value={applicationData.email}
                            onChange={(e) => setApplicationData({...applicationData, email: e.target.value})}
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label htmlFor="currentAddress">Current Address *</Label>
                        <Input
                          id="currentAddress"
                          required
                          placeholder="Street Address, City, State, ZIP"
                          value={applicationData.currentAddress}
                          onChange={(e) => setApplicationData({...applicationData, currentAddress: e.target.value})}
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="phone">Phone *</Label>
                          <Input
                            id="phone"
                            type="tel"
                            required
                            value={applicationData.phone}
                            onChange={(e) => setApplicationData({...applicationData, phone: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label htmlFor="monthlyIncome">Monthly Income *</Label>
                          <Input
                            id="monthlyIncome"
                            type="number"
                            required
                            placeholder="$"
                            value={applicationData.monthlyIncome}
                            onChange={(e) => setApplicationData({...applicationData, monthlyIncome: e.target.value})}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="moveInDate">Desired Move-in Date *</Label>
                          <Input
                            id="moveInDate"
                            type="date"
                            required
                            min={room?.status === 'Available Soon' && room?.available_date
                              ? new Date(room.available_date).toISOString().split('T')[0]
                              : new Date().toISOString().split('T')[0]}
                            value={applicationData.moveInDate}
                            onChange={(e) => setApplicationData({...applicationData, moveInDate: e.target.value})}
                          />
                          {room?.status === 'Available Soon' && room?.available_date && (
                            <p className="text-xs text-orange-600 mt-1">
                              This room becomes available on {new Date(room.available_date).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'long', day: 'numeric', year: 'numeric' })}. Move-in cannot be earlier.
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="border-t pt-4 mt-4">
                        <h3 className="font-semibold mb-3">Furniture (Optional)</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="flex items-center space-x-2">
                            <Checkbox 
                              id="boxSpring"
                              checked={applicationData.furnitureOptions.boxSpring}
                              onCheckedChange={(checked) => setApplicationData({
                                ...applicationData, 
                                furnitureOptions: {...applicationData.furnitureOptions, boxSpring: checked}
                              })}
                            />
                            <Label htmlFor="boxSpring" className="cursor-pointer">Box Spring</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox 
                              id="mattress"
                              checked={applicationData.furnitureOptions.mattress}
                              onCheckedChange={(checked) => setApplicationData({
                                ...applicationData, 
                                furnitureOptions: {...applicationData.furnitureOptions, mattress: checked}
                              })}
                            />
                            <Label htmlFor="mattress" className="cursor-pointer">Mattress</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox 
                              id="dresser"
                              checked={applicationData.furnitureOptions.dresser}
                              onCheckedChange={(checked) => setApplicationData({
                                ...applicationData, 
                                furnitureOptions: {...applicationData.furnitureOptions, dresser: checked}
                              })}
                            />
                            <Label htmlFor="dresser" className="cursor-pointer">Dresser</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox 
                              id="nightStand"
                              checked={applicationData.furnitureOptions.nightStand}
                              onCheckedChange={(checked) => setApplicationData({
                                ...applicationData, 
                                furnitureOptions: {...applicationData.furnitureOptions, nightStand: checked}
                              })}
                            />
                            <Label htmlFor="nightStand" className="cursor-pointer">Night Stand</Label>
                          </div>
                        </div>
                      </div>

                      <div className="border-t pt-4 mt-4">
                        <h3 className="font-semibold mb-3">
                          Background Disclosure <span className="text-red-500">*</span>
                        </h3>
                        <Label className="text-sm">
                          Have you had a prior conviction for any misdemeanor or felony
                          in the past seven (7) years?
                        </Label>
                        <div className="flex items-center gap-4 mt-2" data-testid="prior-conviction-choice">
                          <label className="inline-flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="priorConviction"
                              value="no"
                              className="h-4 w-4 accent-green-600"
                              checked={applicationData.priorConviction === 'no'}
                              onChange={() => setApplicationData({
                                ...applicationData,
                                priorConviction: 'no',
                                convictionDetails: ''
                              })}
                              data-testid="prior-conviction-no"
                            />
                            <span>No</span>
                          </label>
                          <label className="inline-flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="priorConviction"
                              value="yes"
                              className="h-4 w-4 accent-green-600"
                              checked={applicationData.priorConviction === 'yes'}
                              onChange={() => setApplicationData({
                                ...applicationData,
                                priorConviction: 'yes'
                              })}
                              data-testid="prior-conviction-yes"
                            />
                            <span>Yes</span>
                          </label>
                        </div>

                        {applicationData.priorConviction === 'yes' && (
                          <div className="mt-3" data-testid="conviction-details-block">
                            <Label htmlFor="convictionDetails" className="text-sm">
                              Please provide the date, location, and description of the
                              conviction <span className="text-red-500">*</span>
                            </Label>
                            <Textarea
                              id="convictionDetails"
                              value={applicationData.convictionDetails}
                              onChange={(e) => setApplicationData({
                                ...applicationData,
                                convictionDetails: e.target.value
                              })}
                              placeholder="Example: 03/15/2022 — Atlanta, GA — Misdemeanor DUI"
                              rows={4}
                              className="mt-1"
                              required
                              data-testid="conviction-details-input"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Include the date, city/state, and a brief description of the offense.
                            </p>
                          </div>
                        )}

                        <div className="mt-6">
                          <Label className="text-sm">
                            Have you had a prior eviction in the past seven (7) years?
                          </Label>
                          <div className="flex items-center gap-4 mt-2" data-testid="prior-eviction-choice">
                            <label className="inline-flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="priorEviction"
                                value="no"
                                className="h-4 w-4 accent-green-600"
                                checked={applicationData.priorEviction === 'no'}
                                onChange={() => setApplicationData({
                                  ...applicationData,
                                  priorEviction: 'no',
                                  evictionDetails: ''
                                })}
                                data-testid="prior-eviction-no"
                              />
                              <span>No</span>
                            </label>
                            <label className="inline-flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="priorEviction"
                                value="yes"
                                className="h-4 w-4 accent-green-600"
                                checked={applicationData.priorEviction === 'yes'}
                                onChange={() => setApplicationData({
                                  ...applicationData,
                                  priorEviction: 'yes'
                                })}
                                data-testid="prior-eviction-yes"
                              />
                              <span>Yes</span>
                            </label>
                          </div>

                          {applicationData.priorEviction === 'yes' && (
                            <div className="mt-3" data-testid="eviction-details-block">
                              <Label htmlFor="evictionDetails" className="text-sm">
                                Please provide the date, location, and description of the
                                eviction <span className="text-red-500">*</span>
                              </Label>
                              <Textarea
                                id="evictionDetails"
                                value={applicationData.evictionDetails}
                                onChange={(e) => setApplicationData({
                                  ...applicationData,
                                  evictionDetails: e.target.value
                                })}
                                placeholder="Example: 06/2021 — Marietta, GA — Non-payment of rent"
                                rows={4}
                                className="mt-1"
                                required
                                data-testid="eviction-details-input"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                Include the date, city/state, and a brief description of the reason.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="border-t pt-4 mt-4">
                        <h3 className="font-semibold mb-3">Supporting Documents <span className="text-red-500">*</span></h3>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="idDocument">Valid ID (Driver's License, Passport, or State ID)</Label>
                            <Input
                              id="idDocument"
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp,image/*"
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                try {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    console.log('ID file selected:', file.name, file.type, file.size);
                                    setApplicationData(prev => ({...prev, idDocument: file}));
                                  }
                                } catch (err) {
                                  console.error('Error selecting ID file:', err);
                                }
                              }}
                              className="mt-1"
                            />
                            {applicationData.idDocument && (
                              <p className="text-xs text-green-600 mt-1">✓ Selected: {applicationData.idDocument.name}</p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">Accepted formats: PDF, JPG, JPEG, PNG, HEIC (Max 50MB) - <span className="text-red-500 font-medium">Required</span></p>
                          </div>
                          
                          <div>
                            <Label htmlFor="incomeDocument">Proof of Income (Pay Stub, Bank Statement, or Employment Letter)</Label>
                            <Input
                              id="incomeDocument"
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp,image/*"
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                try {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    console.log('Income file selected:', file.name, file.type, file.size);
                                    setApplicationData(prev => ({...prev, incomeDocument: file}));
                                  }
                                } catch (err) {
                                  console.error('Error selecting income file:', err);
                                }
                              }}
                              className="mt-1"
                            />
                            {applicationData.incomeDocument && (
                              <p className="text-xs text-green-600 mt-1">✓ Selected: {applicationData.incomeDocument.name}</p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">Accepted formats: PDF, JPG, JPEG, PNG, HEIC (Max 50MB) - <span className="text-red-500 font-medium">Required</span></p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="additionalInfo">Additional Information</Label>
                        <Textarea
                          id="additionalInfo"
                          placeholder="Tell us about yourself, your rental history, pets, etc..."
                          value={applicationData.additionalInfo}
                          onChange={(e) => setApplicationData({...applicationData, additionalInfo: e.target.value})}
                          rows={4}
                        />
                      </div>

                      <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                        <p className="text-sm text-gray-700">
                          <strong>Application Fee:</strong> $0 (Free)<br />
                          <strong>Processing Time:</strong> 1-2 business days<br />
                          <strong>Note:</strong> All information will be kept confidential and used only for screening purposes.
                        </p>
                      </div>

                      <div className="border-t pt-4 mt-4">
                        <h3 className="font-semibold text-lg mb-4">Application Disclaimer</h3>
                        <div className="space-y-4 text-sm text-gray-700">
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-1 flex items-start">
                              <span className="mr-2">•</span>
                              <span>Truthful Information</span>
                            </h4>
                            <p className="ml-4">All information provided is accurate and complete to the best of your knowledge.</p>
                          </div>
                          
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-1 flex items-start">
                              <span className="mr-2">•</span>
                              <span>Document Verification</span>
                            </h4>
                            <p className="ml-4">Uploaded documents may be reviewed and verified for eligibility.</p>
                          </div>
                          
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-1 flex items-start">
                              <span className="mr-2">•</span>
                              <span>No Guaranteed Approval</span>
                            </h4>
                            <p className="ml-4">Submission does not guarantee approval. Applications are reviewed based on income, availability, and background.</p>
                          </div>
                          
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-1 flex items-start">
                              <span className="mr-2">•</span>
                              <span>Data Privacy</span>
                            </h4>
                            <p className="ml-4">Your personal data is securely stored and will only be used for application and leasing purposes.</p>
                          </div>
                          
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-1 flex items-start">
                              <span className="mr-2">•</span>
                              <span>Background Screening</span>
                            </h4>
                            <p className="ml-4">We may conduct a background and income check if needed.</p>
                          </div>
                          
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-1 flex items-start">
                              <span className="mr-2">•</span>
                              <span>Rent Terms</span>
                            </h4>
                            <p className="ml-4">Upon approval, you will receive lease terms including rent amount, move-in date, and house rules.</p>
                          </div>
                        </div>
                      </div>

                      <Button 
                        type="submit" 
                        className="w-full bg-green-600 hover:bg-green-700 py-6 text-lg"
                        disabled={submitting}
                      >
                        {submitting ? (
                          <span className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Uploading... Please wait
                          </span>
                        ) : 'Submit Application'}
                      </Button>
                      {submitting && (
                        <p className="text-sm text-gray-500 text-center mt-2">
                          This may take a moment if you've attached large files. Please don't close this window.
                        </p>
                      )}
                    </form>
                  </DialogContent>
                </Dialog>

                <Button variant="outline" className="w-full" onClick={() => navigate('/contact')}>
                  Contact Us
                </Button>

                <div className="mt-6 pt-6 border-t">
                  <h3 className="font-semibold mb-3">What's Included?</h3>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-center">
                      <Wifi className="h-4 w-4 mr-2 text-green-600" />
                      High-speed WiFi
                    </li>
                    <li className="flex items-center">
                      <Droplets className="h-4 w-4 mr-2 text-green-600" />
                      All utilities
                    </li>
                    <li className="flex items-center">
                      <Home className="h-4 w-4 mr-2 text-green-600" />
                      Laundry access
                    </li>
                    <li className="flex items-center">
                      <Calendar className="h-4 w-4 mr-2 text-green-600" />
                      Flexible terms
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomDetails;